import { Grid, Select } from '@vtex/admin-ui'
import { useWatch, type FormState } from '@vtex/admin-ui-form'
import { ChangeEvent, useRef } from 'react'
import { FormattedMessage, useIntl, type MessageDescriptor } from 'react-intl'
import { CurrencyInput, NumberInput } from 'ui'
import {
  BenefitChangeModal,
  BenefitChangeModalRef,
} from '../../../../../../BenefitChangeModal'
import { MoreForLessBenefitVariant } from '../../../../../../typings'
import { messages } from './messages'

const DISCOUNT_VARIANTS: Record<
  MoreForLessBenefitVariant,
  {
    labelDescriptor: MessageDescriptor
    renderEditor: React.FC<{ name: string; state: FormState }>
  }
> = {
  [MoreForLessBenefitVariant.PERCENTUAL]: {
    labelDescriptor: messages.discountOptions.percentual,
    renderEditor: function Editor({ name, state }) {
      const { $t } = useIntl()

      return (
        <NumberInput
          name={name}
          state={state}
          prefix="%"
          aria-label={$t(messages.aria.discountValue)}
        />
      )
    },
  },
  [MoreForLessBenefitVariant.MAX_PRICE_PER_ITEM]: {
    labelDescriptor: messages.discountOptions.maxPricePerItem,
    renderEditor: function Editor({ name, state }) {
      const { $t } = useIntl()

      return (
        <CurrencyInput
          name={name}
          state={state}
          aria-label={$t(messages.aria.discountValue)}
        />
      )
    },
  },
}

type MoreForLessDiscountProps = {
  form: FormState
  name: string
}

export function MoreForLessDiscount(props: MoreForLessDiscountProps) {
  const { form, name } = props
  const { $t } = useIntl()

  const discount = useWatch({ form, name })
  const benefitModalRef = useRef<BenefitChangeModalRef>(null)

  const handleBenefitTypeChange = (e: ChangeEvent<HTMLSelectElement>) =>
    benefitModalRef.current?.onBenefitChange(
      e.target.value as MoreForLessBenefitVariant
    )

  function onTypeChange(key: MoreForLessBenefitVariant) {
    form.resetField(name, {
      defaultValue: {
        type: key,
        value: undefined,
      },
    })
  }

  return (
    <>
      <Grid templateColumns="1fr 1fr" columnGap="0.75rem">
        <Select
          value={discount.type}
          onChange={handleBenefitTypeChange}
          aria-label={$t(messages.aria.discountType)}
        >
          {Object.entries(DISCOUNT_VARIANTS).map(
            ([value, { labelDescriptor }]) => (
              <option key={value} value={value}>
                <FormattedMessage {...labelDescriptor} />
              </option>
            )
          )}
        </Select>

        {DISCOUNT_VARIANTS[discount.type].renderEditor({
          name: `${name}.value`,
          state: form,
        })}
      </Grid>
      <BenefitChangeModal ref={benefitModalRef} onConfirm={onTypeChange} />
    </>
  )
}
